<template>
  <div style="padding: 40px; padding-bottom: 0">
    <h1 class="global-h1" style="margin-bottom: 30px">{{ pageTitle }}</h1>

    <template v-if="!isLoading && isCompleted">
      <h2 class="global-h2" style="margin-bottom: 12px">This Creator Visit is complete!</h2>
      <router-link
        :to="{
          name: 'CollabSummaryPublic',
          params: { communityId: summary?.customerId ?? route.params.communityId, collabInputId: summary?.collabInputId ?? route.params.collabInputId },
        }"
      >
        <SrpButton size="small" style="margin-top: 8px">View the summary</SrpButton>
      </router-link>
    </template>

    <template v-else>
      <CollabInputsSummary
        v-if="!isLoading && collabInput"
        class="checklist-guidance-creator__visit-guidance"
        :isCreatorFlow="false"
        :collabInput="collabInput"
        :creatorInput="null"
        :creatorName="null"
      />

      <div v-if="isButtonsVisible" class="buttons-section">
        <div class="divider" style="margin-bottom: 20px"></div>
        <h2>Share with your Stakeholders!</h2>
        <p>
          Copy the link and email to others (it works for users without Shrpa accounts).<br />
          Or print this page as a pdf (this section will be hidden).
        </p>
        <div style="gap: 20px; display: flex">
          <SrpButton @click="copyPageURLToClipboard" :isDisabled="isShowCopied">
            <template #icon>
              <IconEmbedded v-if="isShowCopied" name="check_3-5" :size="20" />
              <IconEmbedded v-else name="copy_2" :size="20" />
            </template>
            Copy Link
          </SrpButton>

          <SrpButton fill="outlined" @click="handlePrint">
            <template #icon>
              <IconEmbedded name="printer_2" :size="20" />
            </template>
            Print
          </SrpButton>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, onBeforeMount } from "vue";
import { AnonymousGuidanceSummaryResponse, CollabInput } from "@contracts/collab";
import { useRoute } from "vue-router";
import CollabsRepo from "@repos/CollabsRepo";
import { RouteHelper } from "@helpers/RouteHelper";

// Components
import CollabInputsSummary from "@views/PaidCollab/CollabInputsSummary.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";

// Router
const route = useRoute();

// Load Anonymous Collab Input ================================================
const isLoading = ref<boolean>(true);
const summary = ref<AnonymousGuidanceSummaryResponse | null>(null);

const isCompleted = computed(() => summary.value?.isCompleted === true);
const collabInput = computed<CollabInput | null>(() => summary.value?.collabInput ?? null);
const pageTitle = computed(() => summary.value?.name ?? collabInput.value?.name ?? "");

onMounted(async () => {
  isLoading.value = true;

  summary.value = await CollabsRepo.loadAnonymousGuidanceSummary(route.params.communityId as string, route.params.collabInputId as string);

  isLoading.value = false;
});

// Hide buttons if the query present ==========================================
const isButtonsVisible = ref<boolean>(true);
const isButtonsVisibleQueryText = "share";

onBeforeMount(() => {
  const queryValue = RouteHelper.getQueryStringOrHashParam(isButtonsVisibleQueryText);

  if (queryValue) {
    isButtonsVisible.value = false;
  }
});

// Copy page URL ==============================================================
const isShowCopied = ref<boolean>(false);

async function copyPageURLToClipboard(): Promise<void> {
  const textToCopy = window.location.origin + window.location.pathname + `?${isButtonsVisibleQueryText}=true`;

  navigator.clipboard.writeText(textToCopy).then(
    () => {
      console.info("Copied to clipboard: " + textToCopy);
    },
    function (err) {
      console.error("Failed to copy to clipboard: ", err);
    }
  );

  isShowCopied.value = true;

  setTimeout(() => {
    isShowCopied.value = false;
  }, 3000);
}

// Handle print ===============================================================
function handlePrint() {
  window.print();
}
</script>

<style scoped>
.buttons-section {
  margin-top: 40px;
  padding-bottom: 80px;
}

.divider {
  border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
}

@media print {
  .buttons-section {
    display: none !important;
  }
}
</style>
